import { HttpClientModule } from '@angular/common/http';
import { ErrorHandler, isDevMode, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { NgbTimepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { WA_POSITION_OPTIONS } from '@ng-web-apis/geolocation';
import { OnlineStatusModule } from 'ngx-online-status';
import { ToastrModule } from 'ngx-toastr';
import { AddCatchDialogComponent } from './add-catch-dialog/add-catch-dialog.component';
import { AddCatchHmsDialogComponent } from './add-catch-hms-dialog/add-catch-hms-dialog.component';
import { AddFisheryDialogComponent } from './add-fishery-dialog/add-fishery-dialog.component';
import { AddSpeciesDialogComponent } from './add-species-dialog/add-species-dialog.component';
import { AddTicketDialogComponent } from './add-ticket-dialog/add-ticket-dialog.component';
import { AddVesselDialogComponent } from './add-vessel-dialog/add-vessel-dialog.component';
import { AppComponent } from './app.component';
import { DateTimePickerComponent } from './date-time-picker/date-time-picker.component';
import { DeliveryDetailComponent } from './delivery-detail/delivery-detail.component';
import { ExpirationDialogComponent } from './delivery-detail/expiration-dialog/expiration-dialog.component';
import { DeliveryListComponent } from './delivery-list/delivery-list.component';
import { ErrorWarningDialogComponent } from './error-warning-dialog/error-warning-dialog.component';
import { GearEditDialogComponent } from './gear-edit-dialog/gear-edit-dialog.component';
import { LonglineFormComponent } from './gear-forms/longline-form/longline-form.component';
import { PelagicFormComponent } from './gear-forms/pelagic-form/pelagic-form.component';
import { PotFormComponent } from './gear-forms/pot-form/pot-form.component';
import { GearSelectDialogComponent } from './gear-select-dialog/gear-select-dialog.component';
import { GearTypeDialogComponent } from './gear-type-dialog/gear-type-dialog.component';
import { HelpAccountComponent } from './help-screen/help-account/help-account.component';
import { HelpAdditionalComponent } from './help-screen/help-additional/help-additional.component';
import { HelpDeliveryComponent } from './help-screen/help-delivery/help-delivery.component';
import { HelpDownloadComponent } from './help-screen/help-download/help-download.component';
import { HelpEditingLogbookComponent } from './help-screen/help-editing-logbook/help-editing-logbook.component';
import { HelpFormComponent } from './help-screen/help-form/help-form.component';
import { HelpGeolocationComponent } from './help-screen/help-geolocation/help-geolocation.component';
import { HelpGuideComponent } from './help-screen/help-guide/help-guide.component';
import { HelpLogbookComponent } from './help-screen/help-logbook/help-logbook.component';
import { HelpMenuComponent } from './help-screen/help-menu/help-menu.component';
import { HelpQuestionsComponent } from './help-screen/help-questions/help-questions.component';
import { HelpScreenComponent } from './help-screen/help-screen.component';
import { HelpSetsComponent } from './help-screen/help-sets/help-sets.component';
import { HelpUpdateComponent } from './help-screen/help-update/help-update.component';
import { HelpVesselsComponent } from './help-screen/help-vessels/help-vessels.component';
import { LoginComponent } from './login/login.component';
import { MaterialModule } from './material/material.module';
import { MenuComponent } from './menu/menu.component';
import { PermitNumDialogComponent } from './permit-num-dialog/permit-num-dialog.component';
import { ProfileComponent } from './profile/profile.component';
import { ResetPasswordDialogComponent } from './reset-password-dialog/reset-password-dialog.component';
import { SetDetailHmsComponent } from './set-detail-hms/set-detail-hms.component';
import { CoordinateDialogComponent } from './set-detail/coordinate-dialog/coordinate-dialog.component';
import { SetDetailComponent } from './set-detail/set-detail.component';
import { SetSelectionDialogComponent } from './set-selection-dialog/set-selection-dialog.component';
import { SettingsComponent } from './settings/settings.component';
import { RouteGuard } from './shared/guard.model';
import { VesselRegResolverService } from './shared/vessel-reg-resolver.service';
import { SignUpComponent } from './sign-up/sign-up.component';
import { SubmitDialogComponent } from './submit-dialog/submit-dialog.component';
import { SummaryReportComponent } from './summary-report/summary-report.component';
import { SummaryReviewComponent } from './summary-review/summary-review.component';
import { TelInputComponent } from './tel-input/tel-input.component';
import { ConfirmDialogComponent } from './vessel-reg/confirm-dialog/confirm-dialog.component';
import { VesselRegComponent } from './vessel-reg/vessel-reg.component';
import { AddUnloadingSpeciesDialogComponent } from './add-unloading-dialog/add-unloading-species-dialog/add-unloading-species-dialog.component';
import { HmsDeliveryDetailComponent } from './hms-delivery-detail/hms-delivery-detail.component';
import { AddUnloadingDialogComponent } from './add-unloading-dialog/add-unloading-dialog.component';
import { TrawlDeliveryComponent } from './trawl-delivery/trawl-delivery.component';
import { SetDetailTrawlComponent } from './set-detail-trawl/set-detail-trawl.component';
import { TrawlDraftsComponent } from './trawl-drafts/trawl-drafts.component';
import { HmsDraftComponent } from './hms-draft/hms-draft.component';
import { SummaryReportYtdComponent } from './summary-report-ytd/summary-report-ytd.component';
import { Insights } from './shared/insights.service';
import { ApplicationinsightsAngularpluginErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import { HelpHmsComponent } from "./help-screen/help-hms/help-hms.component";


const globalRippleConfig: RippleGlobalOptions = {
  disabled: false,
  animation: {
    enterDuration: 100,
    exitDuration: 0
  }
};


@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    SignUpComponent,
    VesselRegComponent,
    AddVesselDialogComponent,
    MenuComponent,
    DeliveryListComponent,
    DeliveryDetailComponent,
    HmsDeliveryDetailComponent,
    TrawlDeliveryComponent,
    SetDetailComponent,
    GearSelectDialogComponent,
    PotFormComponent,
    LonglineFormComponent,
    PelagicFormComponent,
    ErrorWarningDialogComponent,
    SummaryReportComponent,
    SubmitDialogComponent,
    DateTimePickerComponent,
    AddCatchDialogComponent,
    AddCatchHmsDialogComponent,
    AddTicketDialogComponent,
    PermitNumDialogComponent,
    ConfirmDialogComponent,
    SetSelectionDialogComponent,
    CoordinateDialogComponent,
    TelInputComponent,
    ResetPasswordDialogComponent,
    ExpirationDialogComponent,
    HelpScreenComponent,
    SettingsComponent,
    HelpFormComponent,
    HelpGuideComponent,
    HelpDownloadComponent,
    HelpAccountComponent,
    HelpMenuComponent,
    HelpSetsComponent,
    HelpDeliveryComponent,
    HelpLogbookComponent,
    HelpVesselsComponent,
    HelpUpdateComponent,
    HelpAdditionalComponent,
    HelpHmsComponent,
    AddSpeciesDialogComponent,
    HelpQuestionsComponent,
    GearTypeDialogComponent,
    GearEditDialogComponent,
    HelpGeolocationComponent,
    SummaryReviewComponent,
    HelpEditingLogbookComponent,
    ProfileComponent,
    AddFisheryDialogComponent,
    SetDetailHmsComponent,
    AddUnloadingSpeciesDialogComponent,
    AddUnloadingDialogComponent,
    SetDetailTrawlComponent,
    TrawlDraftsComponent,
    HmsDraftComponent,
    SummaryReportYtdComponent
  ],
  imports: [
    BrowserModule,
    ReactiveFormsModule,
    MaterialModule,
    BrowserAnimationsModule,
    FormsModule,
    NgbTimepickerModule,
    OnlineStatusModule,
    HttpClientModule,
    ToastrModule.forRoot(),
    RouterModule.forRoot([
        { path: '', pathMatch: 'full', redirectTo: '/login' },
        { path: 'login', component: LoginComponent },
        { path: 'sign_up', component: SignUpComponent },
        { path: 'vessel_reg/:username', component: VesselRegComponent, resolve: { user: VesselRegResolverService } },
        { path: 'menu', component: MenuComponent, canActivate: [RouteGuard] },
        { path: 'deliveries_list', component: DeliveryListComponent, canActivate: [RouteGuard] },
        { path: 'delivery_detail/:pending/:index', component: DeliveryDetailComponent, canActivate: [RouteGuard] },
        { path: 'trawl_delivery/:pending/:index', component: TrawlDeliveryComponent, canActivate: [RouteGuard] },
        { path: 'trawl_drafts', component: TrawlDraftsComponent, canActivate: [RouteGuard] },
        { path: 'hms_delivery_detail/:pending/:index', component: HmsDeliveryDetailComponent, canActivate: [RouteGuard] },
        { path: 'hms_drafts', component: HmsDraftComponent, canActivate: [RouteGuard] },
        { path: 'set_detail/:index/:isNew', component: SetDetailComponent, canActivate: [RouteGuard] },
        { path: 'set_detail_hms/:index/:isNew', component: SetDetailHmsComponent, canActivate: [RouteGuard] },
        { path: 'set_detail_trawl/:index/:isNew', component: SetDetailTrawlComponent, canActivate: [RouteGuard] },
        { path: 'gear_select_dialog', component: GearSelectDialogComponent, canActivate: [RouteGuard] },
        { path: 'summary_report/:delivery/:index', component: SummaryReportComponent, canActivate: [RouteGuard] },
        { path: 'summary_report_ytd', component: SummaryReportYtdComponent, canActivate: [RouteGuard] },
        { path: 'summary_review/:fishery', component: SummaryReviewComponent, canActivate: [RouteGuard] },
        { path: 'help_screen', component: HelpScreenComponent, canActivate: [RouteGuard] },
        { path: 'settings', component: SettingsComponent, canActivate: [RouteGuard] },
        { path: 'profile', component: ProfileComponent, canActivate: [RouteGuard] },
        { path: '**', component: MenuComponent, canActivate: [RouteGuard] }
    ]),
    ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: !isDevMode(),
        // Register the ServiceWorker immediately
        registrationStrategy: 'registerImmediately'
    })
],
  providers: [
    {
      provide: WA_POSITION_OPTIONS,
      useValue: {enableHighAccuracy: true},
  },
  {provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig},
  Insights,
    {
      provide: ErrorHandler,
      useClass: ApplicationinsightsAngularpluginErrorService,
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
